import type {
  ButtonHTMLAttributes,
  HTMLAttributes,
  InputHTMLAttributes,
  ReactNode,
} from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  Loader2,
  XCircle,
} from 'lucide-react';

export const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};

export const readText = (value: unknown, fallback = ''): string => {
  const next = String(value ?? '').trim();
  return next || fallback;
};

export const extractSpreadsheetFileId = (value: string): string => {
  const normalized = value.trim();
  const match = normalized.match(/\/spreadsheets\/d\/([a-zA-Z0-9-_]+)/i);
  return match?.[1] ?? normalized;
};

export const toOperationSummary = (
  result: Record<string, unknown>,
  label: string,
): string => {
  const status = readText(result.status, 'unknown');
  const message = readText(result.message);
  return message ? `${label}: ${status} - ${message}` : `${label}: ${status}`;
};

export type ReadinessBannerState =
  | 'healthy'
  | 'warning'
  | 'blocker'
  | 'loading';

export const formatBooleanValue = (
  value: boolean | null | undefined,
  {
    trueLabel = 'Yes',
    falseLabel = 'No',
    unknownLabel = 'Unknown',
  }: {
    trueLabel?: string;
    falseLabel?: string;
    unknownLabel?: string;
  } = {},
): string => {
  if (value === true) {
    return trueLabel;
  }
  if (value === false) {
    return falseLabel;
  }
  return unknownLabel;
};

export const formatTimestamp = (value: unknown): string => {
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
    return new Date(value * 1000).toLocaleString();
  }
  const text = readText(value);
  if (!text) {
    return 'Not available';
  }
  const parsed = new Date(text);
  if (!Number.isNaN(parsed.getTime())) {
    return parsed.toLocaleString();
  }
  return text;
};

export const renderUrlValue = (
  value: string | null | undefined,
  testId: string,
): ReactNode => {
  if (!value) {
    return (
      <span className="text-irongrey/60" data-testid={testId}>
        Not configured
      </span>
    );
  }
  return (
    <a
      href={value}
      target="_blank"
      rel="noreferrer"
      className="block truncate text-primary underline"
      title={value}
      data-testid={testId}
    >
      {value}
    </a>
  );
};

export const Card = ({
  children,
  className = '',
  style,
  ...props
}: HTMLAttributes<HTMLDivElement> & { children: ReactNode }) => (
  <div
    className={className}
    style={{
      background: '#ffffff',
      border: '1px solid #e5e7eb',
      borderRadius: 8,
      boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
      padding: 28,
      ...style,
    }}
    {...props}
  >
    {children}
  </div>
);

export const SectionHeader = ({
  title,
  description,
  action,
}: {
  title: string;
  description: string;
  action?: ReactNode;
}) => (
  <div
    style={{
      alignItems: 'flex-start',
      display: 'flex',
      gap: 16,
      justifyContent: 'space-between',
      marginBottom: 24,
    }}
  >
    <div>
      <h3
        style={{
          color: '#404f4d',
          fontSize: 18,
          fontWeight: 600,
          lineHeight: '24px',
          margin: 0,
        }}
      >
        {title}
      </h3>
      <p
        style={{
          color: '#6b7280',
          fontSize: 14,
          lineHeight: '20px',
          margin: '4px 0 0',
        }}
      >
        {description}
      </p>
    </div>
    {action}
  </div>
);

export const Input = ({
  label,
  subLabel,
  className = '',
  style,
  ...props
}: InputHTMLAttributes<HTMLInputElement> & {
  label?: string;
  subLabel?: string;
}) => (
  <div className={className}>
    {label ? (
      <label
        style={{
          color: '#404f4d',
          display: 'block',
          fontSize: 14,
          fontWeight: 500,
          marginBottom: 4,
        }}
      >
        {label}
      </label>
    ) : null}
    <input
      style={{
        border: '1px solid #d1d5db',
        borderRadius: 8,
        boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
        boxSizing: 'border-box',
        color: '#404f4d',
        display: 'block',
        fontFamily: 'inherit',
        fontSize: 14,
        height: 46,
        padding: '0 14px',
        transition: 'border-color 150ms ease, box-shadow 150ms ease',
        width: '100%',
        ...style,
      }}
      {...props}
    />
    {subLabel ? (
      <p
        style={{
          color: '#6b7280',
          fontSize: 12,
          lineHeight: '16px',
          margin: '4px 0 0',
        }}
      >
        {subLabel}
      </p>
    ) : null}
  </div>
);

export const Button = ({
  children,
  variant = 'outline',
  className = '',
  style,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'outline' | 'ghost';
}) => {
  const variantStyle =
    variant === 'primary'
      ? {
          background: '#2697a2',
          border: '1px solid transparent',
          color: '#ffffff',
        }
      : variant === 'ghost'
        ? {
            background: 'transparent',
            border: '1px solid transparent',
            color: '#6b7280',
          }
        : {
            background: '#ffffff',
            border: '1px solid #2697a2',
            color: '#2697a2',
          };
  return (
    <button
      className={className}
      style={{
        alignItems: 'center',
        borderRadius: 6,
        boxShadow:
          variant === 'ghost' ? 'none' : '0 1px 2px rgba(15, 23, 42, 0.06)',
        cursor: props.disabled ? 'not-allowed' : 'pointer',
        display: 'inline-flex',
        fontFamily: 'inherit',
        fontSize: 12,
        fontWeight: 500,
        gap: 8,
        height: 36,
        justifyContent: 'center',
        letterSpacing: '0.12em',
        opacity: props.disabled ? 0.6 : 1,
        padding: '0 18px',
        textTransform: 'uppercase',
        transition:
          'background 150ms ease, border-color 150ms ease, color 150ms ease',
        whiteSpace: 'nowrap',
        ...variantStyle,
        ...style,
      }}
      type={props.type ?? 'button'}
      {...props}
    >
      {children}
    </button>
  );
};

export const ReadinessBanner = ({
  state,
  title,
  description,
  className = '',
  testId,
}: {
  state: ReadinessBannerState;
  title: string;
  description?: string;
  className?: string;
  testId?: string;
}) => {
  const config =
    state === 'healthy'
      ? {
          border: 'border-l-aquamarine',
          bg: 'bg-success-background',
          icon: (
            <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0 text-jungle" />
          ),
          titleClass: 'text-jungle',
        }
      : state === 'warning'
        ? {
            border: 'border-l-coral',
            bg: 'bg-warning-background',
            icon: (
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-irongrey" />
            ),
            titleClass: 'text-irongrey',
          }
        : state === 'loading'
          ? {
              border: 'border-l-border',
              bg: 'bg-muted',
              icon: (
                <Loader2 className="w-4 h-4 mt-0.5 shrink-0 text-text-tertiary animate-spin" />
              ),
              titleClass: 'text-text-secondary',
            }
          : {
              border: 'border-l-strawberry',
              bg: 'bg-error-background',
              icon: (
                <XCircle className="w-4 h-4 mt-0.5 shrink-0 text-strawberry" />
              ),
              titleClass: 'text-strawberry',
            };

  return (
    <div
      data-testid={testId}
      className={[
        'flex items-start gap-3 rounded-lg border border-border border-l-4 px-4 py-3',
        config.border,
        config.bg,
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {config.icon}
      <div className="flex-1 min-w-0">
        <p className={`text-sm font-semibold ${config.titleClass}`}>{title}</p>
        {description ? (
          <p className="text-xs text-text-secondary mt-0.5">{description}</p>
        ) : null}
      </div>
    </div>
  );
};

export function DetailRow({
  label,
  value,
  testId,
}: {
  label: string;
  value: ReactNode;
  testId?: string;
}) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-border py-2 last:border-b-0">
      <span className="text-xs font-semibold uppercase tracking-[0.12em] text-irongrey/55">
        {label}
      </span>
      <span
        className="max-w-[68%] text-right text-sm text-irongrey"
        data-testid={testId}
      >
        {value}
      </span>
    </div>
  );
}
